import { stampCartCompany } from '../cart/cart_company_guard';

/**
 * Update the cart from a set of list items.
 *
 * For each item, set the cart line quantity to the item's quantity: update if
 * the variant is already in the cart, add it if not. Shopify caps quantities to
 * available stock, so we compare the requested quantity against what actually
 * landed in the cart and return a per-item report for the caller to surface.
 *
 * When `listItemIds` is provided (the full variant membership of the list being
 * added), we also report `extras`: cart lines whose variant is NOT part of the
 * list, so the caller can ask the user whether to keep or remove them.
 *
 * @param {Array<{ id: string|number, quantity: number, title?: string }>} items
 * @param {Array<string|number>|null} [listItemIds] full list membership
 * @returns {Promise<{ lines: Array<{ id, title, requested, applied, capped }>, extras: Array<{ id, title, quantity, image, url }> }>}
 */
export async function updateCart(items, listItemIds = null) {
  const list = (items || []).filter((it) => it && it.id);
  const wantExtras = Array.isArray(listItemIds);
  // Nothing to change and no extras to compute → skip the round-trips entirely.
  if (list.length === 0 && !wantExtras) return { lines: [], extras: [] };

  const cartEl =
    document.querySelector('cart-notification') ||
    document.querySelector('cart-drawer');
  const sections = cartEl?.getSectionsToRender
    ? cartEl.getSectionsToRender().map((s) => s.id).toString()
    : '';

  // What's already in the cart (line item .id === variant id), and its qty.
  const cart = await (await fetch('/cart.js')).json();
  const beforeById = new Map(
    (cart.items || []).map((i) => [String(i.id), i.quantity]),
  );
  const inCart = new Set(beforeById.keys());

  const updates = {}; // existing lines → set to the list quantity
  const toAdd = []; // not in cart → add with the list quantity
  list.forEach(({ id, quantity }) => {
    const qty = quantity ?? 0;
    if (inCart.has(String(id))) updates[id] = qty;
    else if (qty > 0) toAdd.push({ id, quantity: qty });
  });

  // Use the last response (with sections) to refresh the cart drawer.
  let data = null;
  if (toAdd.length) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: toAdd, sections }),
    });
    data = await res.json();
  }
  if (Object.keys(updates).length) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, sections }),
    });
    data = await res.json();
  }

  stampCartCompany();
  if (cartEl && data) {
    if (cartEl.classList.contains('is-empty')) {
      cartEl.classList.remove('is-empty');
    }
    // Refresh cart contents/count only — the result modal is the feedback here,
    // so we don't open the drawer (it would cover the modal).
    cartEl.renderContents?.(data);
  }

  // Authoritative final state to see what quantities actually applied.
  const finalCart = await (await fetch('/cart.js')).json();
  const byId = new Map((finalCart.items || []).map((i) => [String(i.id), i]));

  const lines = list
    .filter(({ quantity }) => (quantity ?? 0) > 0)
    .map(({ id, quantity, title }) => {
      const line = byId.get(String(id));
      const applied = line ? line.quantity : 0;
      const before = beforeById.get(String(id)) || 0;
      const requested = quantity ?? 0;
      return {
        id,
        title: line?.product_title || line?.title || title || String(id),
        requested,
        applied,
        before,
        capped: applied < requested,
      };
    });

  // Cart lines that don't belong to the list → the caller can offer keep/remove.
  let extras = [];
  if (wantExtras) {
    const keep = new Set(listItemIds.map((id) => String(id)));
    extras = (finalCart.items || [])
      .filter((line) => !keep.has(String(line.id)))
      .map((line) => ({
        id: line.id,
        title: line.product_title || line.title || String(line.id),
        quantity: line.quantity,
        image: line.image || line.featured_image?.url || '',
        url: line.url || '',
      }));
  }

  return { lines, extras };
}

/**
 * Remove the given variant ids from the cart (set their line quantity to 0) and
 * refresh the cart drawer. Used to drop products that aren't in the list when
 * the user chooses not to keep them.
 *
 * @param {Array<string|number>} ids variant ids to remove
 */
export async function removeCartLines(ids) {
  const list = (ids || []).map((id) => String(id)).filter(Boolean);
  if (list.length === 0) return null;

  const cartEl =
    document.querySelector('cart-notification') ||
    document.querySelector('cart-drawer');
  const sections = cartEl?.getSectionsToRender
    ? cartEl.getSectionsToRender().map((s) => s.id).toString()
    : '';

  const updates = {};
  list.forEach((id) => {
    updates[id] = 0;
  });

  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates, sections }),
  });
  const data = await res.json();

  stampCartCompany();
  if (cartEl && data) {
    cartEl.renderContents?.(data);
  }

  return data;
}

export default updateCart;
